import { LinkButton, Tooltip } from '@pmi/dsm-react';
import {
  RemoveIcon,
  EditIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useEffect, useMemo, useState } from 'react';
import styles from './actionsCell.module.scss';
import { TableCellProps } from '../../models';
import { AccessType } from '../../../../../../shared/utils/mapAccessTypeToLabel';
import useUser from '../../../../../../shared/hooks/useUser';

function ActionsCell({ row, actions, isDeveloper = false }: TableCellProps) {
  const user = useUser();
  const [disabledRemove, setDisabledRemove] = useState(true);
  const [disabledRemoveAction, setDisabledRemoveAction] = useState(
    !!actions.removableAdmin,
  );
  const [disabledEdit, setDisabledEdit] = useState(true);
  const isOwnAccount = useMemo(
    () =>
      user?.id
        ? user.id.toString() === row.original.personId?.toString()
        : false,
    [row.original, user],
  );

  const removeTooltipMsg = useMemo(
    () =>
      isOwnAccount
        ? 'You do not have permission to remove yourself'
        : 'You do not have permission to remove this user. If you believe this is an error, please contact your account administrator',
    [row.original, user],
  );

  const editTooltipMsg = useMemo(
    () =>
      isOwnAccount
        ? 'You do not have permission to edit yourself'
        : 'You do not have permission to edit this user. If you believe this is an error, please contact your account administrator',
    [row.original, user],
  );

  useEffect(() => {
    if (isOwnAccount) {
      setDisabledEdit(true);
      setDisabledRemove(true);
      return;
    }

    const isRowDev = row.original.accessType === AccessType.Dev;
    if (isDeveloper) {
      setDisabledEdit(false);
      setDisabledRemove(false);
    } else if (isRowDev) {
      setDisabledEdit(true);
      setDisabledRemove(true);
    } else {
      setDisabledEdit(true);
      setDisabledRemove(false);
    }
  }, [row.original, isOwnAccount, isDeveloper]);

  useEffect(() => {
    setDisabledRemoveAction(!!actions.removableAdmin);
  }, [actions.removableAdmin]);

  return (
    <div className={`${styles.actionsContainer} d-flex flex-column`}>
      <div className={`${styles.tooltipContainer} d-flex`}>
        <Tooltip
          type="sm"
          content={editTooltipMsg}
          placement="top"
          blockTriggers={!disabledEdit}
          className="darkTooltip"
        >
          <LinkButton
            className={`me-3 ${styles.actionButton}`}
            disabled={disabledEdit}
            size="sm"
            onClick={
              disabledEdit
                ? () => null
                : () => {
                    actions.onEdit({
                      ...row.original,
                      accountRole: row.original.accountRole
                        ? row.original.accountRole.roleId
                        : null,
                    });
                  }
            }
            titleText="Edit"
            icon={EditIcon}
          />
        </Tooltip>
        <Tooltip
          type="sm"
          content={removeTooltipMsg}
          placement="top"
          blockTriggers={!disabledRemove}
          className="darkTooltip"
        >
          <LinkButton
            className={styles.actionButton}
            disabled={disabledRemove || disabledRemoveAction}
            size="sm"
            onClick={
              disabledRemove || disabledRemoveAction
                ? () => null
                : () => {
                    actions.onRemove({
                      ...row.original,
                      accountRole: row.original.accountRole
                        ? row.original.accountRole.roleId
                        : null,
                    });
                  }
            }
            titleText="Remove"
            icon={RemoveIcon}
          />
        </Tooltip>
      </div>
    </div>
  );
}

export default ActionsCell;
